import React, { useEffect, useMemo, useState } from "react";
import Header from "../general/Header";
import Aside from "../general/Aside";
import Footer from "../general/Footer";
import Loading from "../general/Loading";
import Unauthorized from "../general/Unauthorized";
import axios from '../../axios/axiosconf';
import { Link } from "react-router-dom";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function EstadoCuentaReportes() {
    const [loggedIn, setLoggedIn] = useState(false);
    const [hasPermission, setHasPermission] = useState(false);
    const [client, setClient] = useState([]);
    const [items, setItems] = useState([]);
    const [facturas, setFacturas] = useState([]);
    const [presupuestos, setPresupuestos] = useState([]);
    const [loadingDetalle, setLoadingDetalle] = useState(false);
    const [filtroTexto, setFiltroTexto] = useState('');
    const [tipoRegistro, setTipoRegistro] = useState('todos');
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [fechaInicioInput, setFechaInicioInput] = useState('');
    const [fechaFinInput, setFechaFinInput] = useState('');
    const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);

    useEffect(() => {
        if (!localStorage.getItem('userdata')) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }

        const userdata = JSON.parse(localStorage.getItem('userdata'));
        const tienePermiso = (userdata.permissions && Array.isArray(userdata.permissions))
            ? userdata.permissions.some(p => p.slug === 'verInfoPacientes' || p.slug === 'verReportes')
            : (userdata.up && (userdata.up.includes('verInfoPacientes') || userdata.up.includes('verReportes')));

        if (!tienePermiso) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }

        setHasPermission(true);

        Promise.all([
            axios.get('/get-client?uid=' + userdata.user_id),
            axios.get('/pacientes', { params: { uid: userdata.user_id } }),
        ])
            .then(([clienteRes, pacientesRes]) => {
                if (clienteRes.status === 200) setClient(clienteRes.data);
                if (pacientesRes.status === 200) setItems(Array.isArray(pacientesRes.data) ? pacientesRes.data : []);
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                setLoggedIn(true);
            });
    }, []);

    const getNombreCompleto = (item) => {
        const partes = [
            item?.primer_nombre,
            item?.segundo_nombre,
            item?.primer_apellido,
            item?.segundo_apellido,
        ].filter(Boolean);
        return partes.join(' ').trim() || item?.nombre_paciente || 'Sin nombre';
    };

    const formatearFecha = (valor) => {
        if (!valor) return '';
        const fecha = new Date(valor);
        if (Number.isNaN(fecha.getTime())) return '';
        return fecha.toLocaleDateString('es-CO');
    };

    const formatearMoneda = (valor) => `$${Math.round(parseFloat(valor || 0)).toLocaleString('es-CO')}`;
    const formatearFechaIso = (valor) => {
        if (!valor) return '';
        const fecha = new Date(`${valor}T00:00:00`);
        if (Number.isNaN(fecha.getTime())) return '';
        return fecha.toLocaleDateString('es-CO');
    };

    const tipoEtiqueta = (item) => {
        const tipo = Number(item?.tipo_cliente);
        if (tipo === 1) return 'Empresa';
        return 'Paciente';
    };

    const esFacturaNoElectronica = (factura) =>
        factura?.tipo_factura === 'no_electronica' || [4, 5].includes(parseInt(factura?.estado, 10));

    const getTipoFacturaTexto = (factura) => (esFacturaNoElectronica(factura) ? 'No Electrónica' : 'Electrónica');

    const getEstadoFacturaTexto = (estado) => (parseInt(estado, 10) === 3 ? 'Anulada' : 'Generada');

    const getEstadoFacturaBadgeStyle = (estado) => {
        switch (parseInt(estado, 10)) {
            case 3:
                return { background: 'rgba(220, 53, 69, 0.08)', color: '#dc3545', border: '1px solid rgba(220, 53, 69, 0.22)', borderRadius: '10px' };
            default:
                return { background: 'rgba(40, 167, 69, 0.08)', color: '#28a745', border: '1px solid rgba(40, 167, 69, 0.22)', borderRadius: '10px' };
        }
    };

    const cargarDetalle = async (item) => {
        const userdata = JSON.parse(localStorage.getItem('userdata') || '{}');
        if (!item?.id || !userdata?.user_id) return;
        setLoadingDetalle(true);
        setPacienteSeleccionado(item);
        try {
            const [factRes, presRes] = await Promise.all([
                axios.get(`/facturas/paciente/${item.id}`),
                axios.get(`/presupuestos/paciente/${item.id}?uid=${userdata.user_id}`),
            ]);
            setFacturas(Array.isArray(factRes.data) ? factRes.data : []);
            setPresupuestos(Array.isArray(presRes.data) ? presRes.data : []);
        } catch (error) {
            console.log(error);
            setFacturas([]);
            setPresupuestos([]);
        } finally {
            setLoadingDetalle(false);
        }
    };

    const cumpleRango = (fechaValor) => {
        if (!fechaValor) return false;
        const fecha = new Date(fechaValor);
        if (Number.isNaN(fecha.getTime())) return false;

        if (fechaInicio) {
            const inicio = new Date(`${fechaInicio}T00:00:00`);
            if (fecha < inicio) return false;
        }
        if (fechaFin) {
            const fin = new Date(`${fechaFin}T23:59:59`);
            if (fecha > fin) return false;
        }
        return true;
    };

    const facturasFiltradas = useMemo(() => {
        if (!fechaInicio && !fechaFin) return facturas;
        return facturas.filter((f) => cumpleRango(f.fecha_emision || f.created_at));
    }, [facturas, fechaInicio, fechaFin]);

    /** Activo (1) y Pagado (3); se excluyen Generado (0) y Anulado (2). */
    const presupuestosParaReporte = useMemo(() => {
        return presupuestos.filter((p) => [1, 3].includes(Number(p.estado)));
    }, [presupuestos]);

    const presupuestosFiltrados = useMemo(() => {
        if (!fechaInicio && !fechaFin) return presupuestosParaReporte;
        return presupuestosParaReporte.filter((p) => cumpleRango(p.fecha_creacion || p.created_at));
    }, [presupuestosParaReporte, fechaInicio, fechaFin]);

    const saldoFacturas = useMemo(() => {
        return facturasFiltradas.reduce((acc, factura) => {
            const pendiente = (factura.estado === 2 || factura.estado === 5)
                ? parseFloat(factura.valor_pendiente || factura.valor_total || 0)
                : 0;
            return acc + pendiente;
        }, 0);
    }, [facturasFiltradas]);

    const saldoPresupuestos = useMemo(() => {
        return presupuestosFiltrados.reduce((acc, pres) => {
            const esActivo = Number(pres.estado) === 1;
            const saldo = esActivo ? parseFloat(pres.saldo_disponible || 0) : 0;
            return acc + saldo;
        }, 0);
    }, [presupuestosFiltrados]);

    const totalFacturado = useMemo(() => {
        return facturasFiltradas.reduce((acc, factura) => {
            if (parseInt(factura.estado, 10) === 3) return acc; // Excluir anuladas
            return acc + parseFloat(factura.valor_total || 0);
        }, 0);
    }, [facturasFiltradas]);

    const saldoPendiente = useMemo(() => saldoFacturas + saldoPresupuestos, [saldoFacturas, saldoPresupuestos]);

    const itemsFiltrados = useMemo(() => {
        const texto = filtroTexto.trim().toLowerCase();
        return items.filter((item) => {
            const nombre = getNombreCompleto(item).toLowerCase();
            const doc = String(item?.numero_documento || '').toLowerCase();
            const cel = String(item?.celular || '').toLowerCase();
            const em = String(item?.email || '').toLowerCase();
            const coincideTexto = !texto || nombre.includes(texto) || doc.includes(texto) || cel.includes(texto) || em.includes(texto);
            const esEmpresa = Number(item?.tipo_cliente) === 1;
            const coincideTipo = tipoRegistro === 'todos'
                || (tipoRegistro === 'pacientes' && !esEmpresa)
                || (tipoRegistro === 'empresas' && esEmpresa);
            return coincideTexto && coincideTipo;
        });
    }, [items, filtroTexto, tipoRegistro]);

    const tituloRango = useMemo(() => {
        if (fechaInicio && fechaFin) {
            return `ESTADO DE CUENTA DE FECHA ${formatearFechaIso(fechaInicio)} A ${formatearFechaIso(fechaFin)}`;
        }
        if (fechaInicio) {
            return `ESTADO DE CUENTA DESDE ${formatearFechaIso(fechaInicio)}`;
        }
        if (fechaFin) {
            return `ESTADO DE CUENTA HASTA ${formatearFechaIso(fechaFin)}`;
        }
        return 'ESTADO DE CUENTA';
    }, [fechaInicio, fechaFin]);

    const exportarPdf = async () => {
        if (!pacienteSeleccionado) return;
        try {
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.getWidth();
            let y = 14;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(18);
            doc.text('ESTADO DE CUENTA', 14, y);
            y += 8;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text(`Paciente: ${getNombreCompleto(pacienteSeleccionado)}`, 14, y);
            doc.text(`Documento: ${pacienteSeleccionado?.numero_documento || 'N/A'}`, 90, y);
            doc.text(`Fecha reporte: ${formatearFecha(new Date())}`, 150, y);
            y += 5;
            doc.text(`Telefono: ${pacienteSeleccionado?.telefono || 'N/A'}`, 14, y);
            doc.text(`Celular: ${pacienteSeleccionado?.celular || pacienteSeleccionado?.telefono || 'N/A'}`, 90, y);
            y += 5;
            doc.text(`Email: ${pacienteSeleccionado?.email || 'N/A'}`, 14, y);
            y += 6;

            doc.setFillColor(230, 230, 230);
            doc.rect(14, y, pageWidth - 28, 8, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10);
            doc.text(tituloRango, 16, y + 5.2);
            y += 12;

            autoTable(doc, {
                startY: y,
                theme: 'grid',
                head: [['Saldo Pendiente', 'Saldo Total', 'Total Facturado']],
                body: [[formatearMoneda(saldoFacturas), formatearMoneda(saldoPendiente), formatearMoneda(totalFacturado)]],
                styles: { halign: 'center', fontSize: 10, cellPadding: 2.5 },
                headStyles: { fillColor: [229, 231, 235], textColor: [31, 41, 55] },
                alternateRowStyles: { fillColor: [245, 245, 245] },
            });

            y = (doc.lastAutoTable?.finalY || y) + 6;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('PRESUPUESTOS', 14, y);
            y += 2;

            autoTable(doc, {
                startY: y,
                theme: 'grid',
                head: [['N° Presupuesto', 'Fecha', 'Pagado', 'Total', 'Pendiente']],
                body: presupuestosFiltrados.length
                    ? presupuestosFiltrados.map((pres) => {
                        const total = parseFloat(pres.valor_total || 0);
                        const pagado = parseFloat(pres.valor_consumido || 0);
                        const pendiente = parseFloat(pres.saldo_disponible || 0);
                        return [
                            pres.numero_presupuesto || `#${pres.id}`,
                            formatearFecha(pres.fecha_creacion),
                            formatearMoneda(pagado),
                            formatearMoneda(total),
                            formatearMoneda(pendiente),
                        ];
                    })
                    : [['-', '-', '-', '-', '-']],
                styles: { halign: 'center', fontSize: 9.5, cellPadding: 2.2 },
                headStyles: { fillColor: [229, 231, 235], textColor: [31, 41, 55] },
                alternateRowStyles: { fillColor: [249, 250, 251] },
            });

            y = (doc.lastAutoTable?.finalY || y) + 6;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('FACTURAS', 14, y);
            y += 2;

            autoTable(doc, {
                startY: y,
                theme: 'grid',
                head: [['N° Factura', 'Fecha', 'Tipo', 'Total', 'Estado']],
                body: facturasFiltradas.length
                    ? facturasFiltradas.map((factura) => {
                        const numero = factura.prefijo && factura.numero_factura
                            ? `${factura.prefijo}-${factura.numero_factura}`
                            : (factura.numero_factura || `#${factura.id}`);
                        return [
                            numero,
                            formatearFecha(factura.fecha_emision),
                            getTipoFacturaTexto(factura),
                            formatearMoneda(factura.valor_total),
                            getEstadoFacturaTexto(factura.estado),
                        ];
                    })
                    : [['-', '-', '-', '-', '-']],
                styles: { halign: 'center', fontSize: 9.5, cellPadding: 2.2 },
                headStyles: { fillColor: [229, 231, 235], textColor: [31, 41, 55] },
                alternateRowStyles: { fillColor: [249, 250, 251] },
            });

            doc.save(`estado-cuenta-${pacienteSeleccionado.id}.pdf`);
        } catch (error) {
            console.log(error);
            alert('No fue posible exportar el PDF');
        }
    };

    if (!loggedIn) return <Loading />;
    if (!hasPermission) return <Unauthorized />;

    return (
        <div className="wrapper hold-transition" style={{ fontSize: '13px' }}>
            <Header client={client} />
            <Aside client={client} activelink="reports" />

            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-12">
                                <ol className="breadcrumb float-sm-left mb-0">
                                    <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas">Reportes</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas-contables">Contables</Link></li>
                                    <li className="breadcrumb-item active">Estado de cuenta</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        {!pacienteSeleccionado && (
                            <div className="card">
                                <div className="card-header d-flex align-items-center">
                                    <Link to="/estadisticas-contables" className="btn btn-outline-secondary btn-sm mr-3" title="Volver">
                                        <i className="fas fa-arrow-left"></i>
                                    </Link>
                                    <h3 className="card-title mb-0"><i className="fas fa-users mr-2"></i>Pacientes y Empresas</h3>
                                </div>
                                <div className="card-body">
                                    <div className="row mb-3">
                                        <div className="col-md-8 mb-2 mb-md-0">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Buscar por nombre, documento, celular o email"
                                                value={filtroTexto}
                                                onChange={(e) => setFiltroTexto(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <select className="form-control" value={tipoRegistro} onChange={(e) => setTipoRegistro(e.target.value)}>
                                                <option value="todos">Todos</option>
                                                <option value="pacientes">Solo pacientes</option>
                                                <option value="empresas">Solo empresas</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover">
                                            <thead>
                                                <tr>
                                                    <th>Tipo</th>
                                                    <th>Nombre</th>
                                                    <th>Documento</th>
                                                    <th>Celular</th>
                                                    <th>Email</th>
                                                    <th className="text-center">Acción</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {itemsFiltrados.length === 0 && (
                                                    <tr>
                                                        <td colSpan="6" className="text-center text-muted">No hay registros para mostrar.</td>
                                                    </tr>
                                                )}
                                                {itemsFiltrados.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{tipoEtiqueta(item)}</td>
                                                        <td>{getNombreCompleto(item)}</td>
                                                        <td>{item.numero_documento || 'N/A'}</td>
                                                        <td>{item.celular || item.telefono || 'N/A'}</td>
                                                        <td>{item.email || 'N/A'}</td>
                                                        <td className="text-center">
                                                            <button
                                                                className="btn btn-info btn-sm"
                                                                onClick={() => cargarDetalle(item)}
                                                                title="Estado de cuenta"
                                                                aria-label="Estado de cuenta"
                                                            >
                                                                <i className="fas fa-file-invoice-dollar"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {pacienteSeleccionado && (
                            <div>
                                <div className="card" style={{ borderRadius: '12px', border: '1px solid #e9ecef' }}>
                                    <div className="card-header d-flex align-items-center flex-wrap">
                                        <button
                                            className="btn btn-outline-secondary btn-sm mr-3 mb-2 mb-md-0"
                                            onClick={() => {
                                                setPacienteSeleccionado(null);
                                                setFacturas([]);
                                                setPresupuestos([]);
                                                setFechaInicio('');
                                                setFechaFin('');
                                                setFechaInicioInput('');
                                                setFechaFinInput('');
                                            }}
                                            title="Volver"
                                            aria-label="Volver"
                                        >
                                            <i className="fas fa-arrow-left"></i>
                                        </button>
                                        <h3 className="card-title mb-0">
                                            Estado de cuenta
                                        </h3>
                                    </div>
                                    <div className="card-body">
                                        <style>{`
                                            .ec-info-card, .ec-saldo-card, .ec-note-card {
                                                border: 1px solid #d0d7de;
                                                border-radius: 10px;
                                                background: #fff;
                                            }
                                            .ec-info-title, .ec-saldo-title {
                                                font-weight: 700;
                                                color: #334155;
                                                border-bottom: 2px solid #17a2b8;
                                                padding-bottom: 8px;
                                                margin-bottom: 12px;
                                            }
                                            .ec-note-card { padding: 12px 14px; color: #1e293b; }
                                            .ec-table col:nth-child(1) { width: 24%; }
                                            .ec-table col:nth-child(2) { width: 18%; }
                                            .ec-table col:nth-child(3) { width: 18%; }
                                            .ec-table col:nth-child(4) { width: 20%; }
                                            .ec-table col:nth-child(5) { width: 20%; }
                                        `}</style>
                                        <div className="ec-info-card p-3 mb-3">
                                            <h4 className="ec-info-title mb-3"><i className="fas fa-user mr-2"></i>{getNombreCompleto(pacienteSeleccionado)}</h4>
                                            <div className="row">
                                                <div className="col-md-3"><strong>Documento:</strong> {pacienteSeleccionado.numero_documento || 'N/A'}</div>
                                                <div className="col-md-3"><strong>Teléfono:</strong> {pacienteSeleccionado.telefono || 'N/A'}</div>
                                                <div className="col-md-3"><strong>Celular:</strong> {pacienteSeleccionado.celular || pacienteSeleccionado.telefono || 'N/A'}</div>
                                                <div className="col-md-3"><strong>Email:</strong> {pacienteSeleccionado.email || 'N/A'}</div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4 mb-3">
                                                <div className="ec-saldo-card p-3 h-100">
                                                    <h4 className="ec-saldo-title mb-2"><i className="fas fa-file-invoice-dollar mr-2"></i>Saldo Pendiente</h4>
                                                    <h3 className="mb-0">{formatearMoneda(saldoFacturas)}</h3>
                                                </div>
                                            </div>
                                            <div className="col-md-4 mb-3">
                                                <div className="ec-saldo-card p-3 h-100">
                                                    <h4 className="ec-saldo-title mb-2"><i className="fas fa-wallet mr-2"></i>Saldo Total</h4>
                                                    <h3 className="mb-0">{formatearMoneda(saldoPendiente)}</h3>
                                                </div>
                                            </div>
                                            <div className="col-md-4 mb-3">
                                                <div className="ec-saldo-card p-3 h-100">
                                                    <h4 className="ec-saldo-title mb-2"><i className="fas fa-calculator mr-2"></i>Total Facturado</h4>
                                                    <h3 className="mb-0">{formatearMoneda(totalFacturado)}</h3>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="ec-info-card p-3 mb-3">
                                            <div className="row mb-0">
                                                <div className="col-md-12 mb-2">
                                                    <strong>{tituloRango}</strong>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-3 mb-2 mb-md-0">
                                                    <label className="mb-1">Fecha inicio</label>
                                                    <input type="date" className="form-control" value={fechaInicioInput} onChange={(e) => setFechaInicioInput(e.target.value)} />
                                                </div>
                                                <div className="col-md-3 mb-2 mb-md-0">
                                                    <label className="mb-1">Fecha fin</label>
                                                    <input type="date" className="form-control" value={fechaFinInput} onChange={(e) => setFechaFinInput(e.target.value)} />
                                                </div>
                                                <div className="col-md-6 d-flex align-items-end">
                                                    <button
                                                        className="btn btn-info mr-2"
                                                        onClick={() => {
                                                            setFechaInicio(fechaInicioInput);
                                                            setFechaFin(fechaFinInput);
                                                        }}
                                                    >
                                                        <i className="fas fa-filter mr-1"></i>Filtrar
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-secondary mr-2"
                                                        onClick={() => {
                                                            setFechaInicio('');
                                                            setFechaFin('');
                                                            setFechaInicioInput('');
                                                            setFechaFinInput('');
                                                        }}
                                                    >
                                                        Limpiar
                                                    </button>
                                                    <button className="btn btn-danger" onClick={exportarPdf}>
                                                        <i className="fas fa-file-pdf mr-1"></i>Descargar
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {loadingDetalle && <p className="text-muted mb-0">Cargando estado de cuenta...</p>}

                                        {!loadingDetalle && (
                                            <>
                                                <div className="card" style={{ border: '1px solid #e9ecef' }}>
                                                    <div className="card-header"><h3 className="card-title mb-0">Presupuestos</h3></div>
                                                    <div className="card-body p-0">
                                                        <table className="table table-sm mb-0 ec-table">
                                                            <colgroup>
                                                                <col />
                                                                <col />
                                                                <col />
                                                                <col />
                                                                <col />
                                                            </colgroup>
                                                            <thead>
                                                                <tr>
                                                                    <th className="text-center">N° Presupuesto</th>
                                                                    <th className="text-center">Fecha</th>
                                                                    <th className="text-center">Pagado</th>
                                                                    <th className="text-center">Total</th>
                                                                    <th className="text-center">Pendiente</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {presupuestosFiltrados.length === 0 && (
                                                                    <tr><td colSpan="5" className="text-center text-muted">No hay presupuestos (activos o pagados) para el rango seleccionado.</td></tr>
                                                                )}
                                                                {presupuestosFiltrados.map((pres) => {
                                                                    const total = parseFloat(pres.valor_total || 0);
                                                                    const pagado = parseFloat(pres.valor_consumido || 0);
                                                                    const pendiente = parseFloat(pres.saldo_disponible || 0);
                                                                    return (
                                                                        <tr key={pres.id}>
                                                                            <td className="text-center">{pres.numero_presupuesto || `#${pres.id}`}</td>
                                                                            <td className="text-center">{formatearFecha(pres.fecha_creacion)}</td>
                                                                            <td className="text-center">{formatearMoneda(pagado)}</td>
                                                                            <td className="text-center">{formatearMoneda(total)}</td>
                                                                            <td className="text-center">{formatearMoneda(pendiente)}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>

                                                <div className="card" style={{ border: '1px solid #e9ecef' }}>
                                                    <div className="card-header"><h3 className="card-title mb-0">Facturas</h3></div>
                                                    <div className="card-body p-0">
                                                        <table className="table table-sm mb-0 ec-table">
                                                            <colgroup>
                                                                <col />
                                                                <col />
                                                                <col />
                                                                <col />
                                                                <col />
                                                            </colgroup>
                                                            <thead>
                                                                <tr>
                                                                    <th className="text-center">N° Factura</th>
                                                                    <th className="text-center">Fecha</th>
                                                                    <th className="text-center">Tipo</th>
                                                                    <th className="text-center">Total</th>
                                                                    <th className="text-center">Estado</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {facturasFiltradas.length === 0 && (
                                                                    <tr><td colSpan="5" className="text-center text-muted">No hay facturas para el rango seleccionado.</td></tr>
                                                                )}
                                                                {facturasFiltradas.map((factura) => {
                                                                    const numero = factura.prefijo && factura.numero_factura
                                                                        ? `${factura.prefijo}-${factura.numero_factura}`
                                                                        : (factura.numero_factura || `#${factura.id}`);
                                                                    return (
                                                                        <tr key={factura.id}>
                                                                            <td className="text-center">{numero}</td>
                                                                            <td className="text-center">{formatearFecha(factura.fecha_emision)}</td>
                                                                            <td className="text-center">{getTipoFacturaTexto(factura)}</td>
                                                                            <td className="text-center">{formatearMoneda(factura.valor_total)}</td>
                                                                            <td className="text-center">
                                                                                <span
                                                                                    className="px-2 py-1"
                                                                                    style={{
                                                                                        ...getEstadoFacturaBadgeStyle(factura.estado),
                                                                                        display: 'inline-block',
                                                                                        fontSize: '12px',
                                                                                        fontWeight: 600,
                                                                                    }}
                                                                                >
                                                                                    {getEstadoFacturaTexto(factura.estado)}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <Footer showToast={false} delay={0} />
        </div>
    );
}

export default EstadoCuentaReportes;
